import api from './auth.api';

export interface Site {
  id: number;
  crm_site_id: number;
  name: string;
  timezone: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ReminderPolicy {
  id: number;
  site_id: number | null;
  site_name: string | null;
  name: string;
  is_active: boolean;
  cron_expression: string;
  timezone: string;
  dry_run: boolean;
  send_due_today: boolean;
  send_overdue: boolean;
  send_before_due: boolean;
  before_due_days: number[];
  max_reminders_per_invoice: number | null;
  min_hours_between_reminders: number;
  eligible_subscriber_statuses: string[];
  stopped_subscriber_statuses: string[];
  thank_you_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ReminderAutomationSettings {
  enabled: boolean;
  frequency_days: number;
  run_times: string[];
  timezone: string;
}

export interface UpcomingDueReminderSettings {
  enabled: boolean;
  run_time: string;
  days_before_due: number;
  frequency_days: number;
  timezone: string;
}

export interface CrmSyncScheduleSettings {
  enabled: boolean;
  run_time: string;
  timezone: string;
}

export interface TestMsg91Request {
  phone_number: string;
  template_name: string;
  namespace: string;
  payment_url: string;
  customer_name: string;
  invoice_list: string;
  total_due_amount: string;
}

export const settingsApi = {
  getSites: async (): Promise<Site[]> => {
    const response = await api.get<{ success: boolean; data: Site[] }>('/settings/sites');
    return response.data.data;
  },

  getReminderPolicies: async (): Promise<ReminderPolicy[]> => {
    const response = await api.get<{ success: boolean; data: ReminderPolicy[] }>(
      '/settings/reminder-policy'
    );
    return response.data.data;
  },

  getReminderAutomation: async (): Promise<ReminderAutomationSettings> => {
    const response = await api.get<{ success: boolean; data: ReminderAutomationSettings }>(
      '/settings/reminder-automation'
    );
    return response.data.data;
  },

  updateReminderAutomation: async (
    settings: ReminderAutomationSettings
  ): Promise<ReminderAutomationSettings> => {
    const response = await api.patch<{ success: boolean; data: ReminderAutomationSettings }>(
      '/settings/reminder-automation',
      settings
    );
    return response.data.data;
  },

  getUpcomingDueReminder: async (): Promise<UpcomingDueReminderSettings> => {
    const response = await api.get<{ success: boolean; data: UpcomingDueReminderSettings }>(
      '/settings/upcoming-due-reminder'
    );
    return response.data.data;
  },

  updateUpcomingDueReminder: async (
    settings: UpcomingDueReminderSettings
  ): Promise<UpcomingDueReminderSettings> => {
    const response = await api.patch<{ success: boolean; data: UpcomingDueReminderSettings }>(
      '/settings/upcoming-due-reminder',
      settings
    );
    return response.data.data;
  },

  getCrmSyncSchedule: async (): Promise<CrmSyncScheduleSettings> => {
    const response = await api.get<{ success: boolean; data: CrmSyncScheduleSettings }>(
      '/settings/crm-sync-schedule'
    );
    return response.data.data;
  },

  updateCrmSyncSchedule: async (
    settings: CrmSyncScheduleSettings
  ): Promise<CrmSyncScheduleSettings> => {
    const response = await api.patch<{ success: boolean; data: CrmSyncScheduleSettings }>(
      '/settings/crm-sync-schedule',
      settings
    );
    return response.data.data;
  },

  testCrmConnection: async (siteId?: number): Promise<{ connected: boolean; message: string }> => {
    const response = await api.post<{
      success: boolean;
      data: { connected: boolean; message: string };
    }>('/settings/test-crm-connection', { site_id: siteId });
    return response.data.data;
  },

  testMsg91Message: async (
    payload: TestMsg91Request
  ): Promise<{
    sent: boolean;
    simulated?: boolean;
    message: string;
    requestId?: string;
    messageId?: string;
  }> => {
    const response = await api.post<{
      success: boolean;
      data: {
        sent: boolean;
        simulated?: boolean;
        message: string;
        requestId?: string;
        messageId?: string;
      };
    }>('/settings/test-msg91-message', payload);
    return response.data.data;
  },
};

export default settingsApi;
